import React, { useEffect, useMemo, useRef, useState, useImperativeHandle } from 'react';
import { Modal, Space, Button, Typography, Alert, InputNumber, Divider, Switch, ColorPicker, Checkbox } from 'antd';
import CurveOverlay from './CurveOverlay';
import { captureElementToPngDataUrl, downloadTextFile, resolveFilenamePrefix } from '../utils/exportUtils';
import { buildSeries, analyzeSelectivity, recommendUpstreamTMS, buildSelectivityMatrix } from '../utils/tccUtils';
import { useI18n } from '../i18n/i18n.jsx';

const { Text } = Typography;

const palette = [
  '#1677ff', '#faad14', '#52c41a', '#eb2f96', '#13c2c2',
  '#722ed1', '#f5222d', '#2f54eb', '#a0d911', '#fa8c16'
];

const TccOverlayModal = React.forwardRef(({ visible, onClose, records = [], filenamePrefix = '', defaultMinMargin = 0.3 }, ref) => {
  const containerRef = useRef(null);
  const { t } = useI18n();
  const [minMargin, setMinMargin] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? (JSON.parse(saved).minMargin ?? defaultMinMargin ?? 0.3) : (defaultMinMargin ?? 0.3); } catch { return defaultMinMargin ?? 0.3; }
  });
  const [showLegend, setShowLegend] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? !!JSON.parse(saved).showLegend : true; } catch { return true; }
  });
  const [zoomX, setZoomX] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? (JSON.parse(saved).zoomX || 1) : 1; } catch { return 1; }
  });
  const [zoomY, setZoomY] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? (JSON.parse(saved).zoomY || 1) : 1; } catch { return 1; }
  });
  const [panX, setPanX] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? (JSON.parse(saved).panX || 0) : 0; } catch { return 0; }
  });
  const [panY, setPanY] = useState(() => {
    const saved = localStorage.getItem('tccOverlaySettings');
    try { return saved ? (JSON.parse(saved).panY || 0) : 0; } catch { return 0; }
  });
  const [colorMap, setColorMap] = useState(() => {
    const saved = localStorage.getItem('tccOverlayColors');
    try { return saved ? JSON.parse(saved) : {}; } catch { return {}; }
  });
  const [hiddenLabels, setHiddenLabels] = useState(() => {
    const saved = localStorage.getItem('tccOverlayHidden');
    try { return saved ? JSON.parse(saved) : []; } catch { return []; }
  });

  const series = useMemo(() => {
    const arr = [];
    (records || []).forEach((rec, idx) => {
      try {
        const results = rec?.results || {};
        const axes = results?.axesType || 'I-t';
        const hasCurve = !!(results?.pickupCurrent && results?.timeMultiplier && results?.curveType);
        const shouldInclude = (
          (axes === 'I-t' && hasCurve) ||
          (axes === 'Id-Ir' && !!results?.characteristic) ||
          (axes === 'Z' && Array.isArray(results?.zones) && results.zones.length > 0)
        );
        if (shouldInclude) {
          const label = (rec?.result?.name && rec.result.name.trim()) ? rec.result.name.trim() : `记录${idx + 1}`;
          const key = String(rec?.id ?? label);
          // 优先采用记录自带颜色（meta.color 或 results.color），否则回退到颜色映射与默认调色板
          const recordColor = (rec?.meta?.color || rec?.color || results?.color);
          const color = (recordColor || colorMap[key] || colorMap[label] || palette[idx % palette.length]);
          const serie = buildSeries({ label, color, results });
          Object.assign(serie, { key });
          arr.push(serie);
        }
      } catch (_) { /* ignore one bad record */ }
    });
    return arr;
  }, [records, colorMap]);

  const visibleSeries = useMemo(() => {
    const axesChosen = series?.[0]?.axesType;
    return (series || []).filter(s => s.axesType === axesChosen).filter(s => !(hiddenLabels.includes(s.key) || hiddenLabels.includes(s.label)));
  }, [series, hiddenLabels]);

  useEffect(() => {
    // 持久化颜色映射：新出现的标签按当前颜色缓存
    const next = { ...colorMap };
    let changed = false;
    (series || []).forEach(s => {
      const k = s.key || s.label;
      if (k && s.color && next[k] !== s.color) {
        next[k] = s.color; changed = true;
      }
    });
    if (changed) {
      setColorMap(next);
      try { localStorage.setItem('tccOverlayColors', JSON.stringify(next)); } catch {}
    }
  }, [series]);

  useEffect(() => {
    try { localStorage.setItem('tccOverlayHidden', JSON.stringify(hiddenLabels)); } catch {}
  }, [hiddenLabels]);

  const analyses = useMemo(() => {
    if (!visibleSeries || visibleSeries.length < 2) return [];
    const down = visibleSeries[0];
    return visibleSeries.slice(1).map((up, i) => {
      const an = analyzeSelectivity(down, up, { minMargin });
      const rec = recommendUpstreamTMS(
        { pickup: down.pickup, TMS: down.TMS, curveType: down.curveType },
        { pickup: up.pickup, TMS: up.TMS, curveType: up.curveType },
        { minMargin }
      );
      return { index: i + 1, upstream: up.label, analysis: an, recommendation: rec };
    });
  }, [visibleSeries, minMargin]);

  // 多级选择性校核矩阵（按相邻链路）
  const matrix = useMemo(() => {
    if (!visibleSeries || visibleSeries.length < 2) return { pairs: [], overallOk: false, adjustments: [], finalSeries: [] };
    return buildSelectivityMatrix(visibleSeries, { minMargin, sequentialAdjust: true });
  }, [visibleSeries, minMargin]);

  const sortText = useMemo(() => {
    try {
  const sortMode = records?.[0]?.meta?.sortMode;
  return sortMode === 'bySelectionOrder' ? t('overlay.sort.by_selection_order_short') : t('overlay.sort.by_rated_current_asc_short');
    } catch (_) { return ''; }
  }, [records]);

  const selectionOrderLabels = useMemo(() => {
    try {
      const arr = records?.[0]?.meta?.selectionOrderLabels;
      return Array.isArray(arr) ? arr : [];
    } catch (_) { return []; }
  }, [records]);

  const resolvedPrefix = useMemo(() => {
    return resolveFilenamePrefix(filenamePrefix, { sort: sortText });
  }, [filenamePrefix, sortText]);

  const handleExportPng = async () => {
    try {
      const el = containerRef.current;
      if (!el) return;
      const dataUrl = await captureElementToPngDataUrl(el);
      const a = document.createElement('a');
      a.href = dataUrl;
      const prefix = (resolvedPrefix && resolvedPrefix.trim()) ? (resolvedPrefix.trim() + '_') : '';
      a.download = `${prefix}TCC_Overlay_${Date.now()}.png`;
      a.click();
    } catch (e) {
      // 无需提示组件外 message，这里保持静默失败
      console.error('导出叠加图失败', e);
    }
  };

  const handleExportMd = async () => {
    try {
      const lines = [];
      lines.push(`# TCC 批量叠加与选择性校核\n`);
      if (resolvedPrefix && resolvedPrefix.trim()) {
        lines.push(`导出前缀（解析后）：${resolvedPrefix.trim()}`);
      }
      // 排序方式说明（取第一条记录的 meta.sortMode）
      try {
  const sortMode = records?.[0]?.meta?.sortMode;
  const sortTextLocal = sortMode === 'bySelectionOrder' ? t('overlay.sort.by_selection_order_short') : t('overlay.sort.by_rated_current_asc_short');
  if (sortMode) lines.push(`${t('overlay.md.sort_mode_prefix')}${sortTextLocal}`);
  const orderLabels = records?.[0]?.meta?.selectionOrderLabels;
  if (Array.isArray(orderLabels) && orderLabels.length > 0) {
    lines.push(`${t('overlay.md.selection_order_prefix')}${orderLabels.join(' → ')}`);
  }
      } catch (_) {}
      lines.push(`叠加曲线数：${series.length}`);
      lines.push('');
      lines.push('## 曲线列表');
      series.forEach((s, idx) => {
        lines.push(`- ${idx === 0 ? '**下级(基准)**' : '**上级**'}：${s.label}，pickup=${s.pickup}A，TMS=${s.TMS}，曲线=${s.curveType}`);
      });
      lines.push('');
      // 设备详情
      lines.push('## 设备详情');
      (records || []).forEach((rec, idx) => {
        const m = rec?.meta || {};
        const name = (rec?.result?.name || `记录${idx + 1}`);
        const rated = (m.ratedCurrent ?? '—');
        const ct = (m.ctRatio ?? '—');
        const pt = (m.ptRatio ?? '—');
        const rLine = (m.lineR_ohm ?? '—');
        const xLine = (m.lineX_ohm ?? '—');
        const Un = (m.systemVoltage_kV ?? '—');
        lines.push(`- ${idx === 0 ? '**下级(基准)**' : '**上级**'}：${name}`);
        lines.push(`  - 额定电流(A)：${rated}`);
        lines.push(`  - CT变比：${ct}；PT变比：${pt}`);
        lines.push(`  - 系统电压(kV)：${Un}`);
        lines.push(`  - 线路阻抗(Ω)：R=${rLine}，X=${xLine}`);
      });
      lines.push('');
      lines.push(`## 选择性校核（最小裕度 ${minMargin}s）`);
      analyses.forEach((item, idx) => {
        const a = item.analysis;
        const r = item.recommendation;
        lines.push(`- 与上级“${item.upstream}”对比：`);
        lines.push(`  - 最差裕度：${a.worstMargin === null ? '—' : `${a.worstMargin}s`}，发生在 I≈${a.worstAtI || '—'}A`);
        lines.push(`  - 是否满足：${a.ok ? '是' : '否'}`);
        lines.push(`  - 建议上级 TMS：${r.recommended ?? '—'}${r.capped ? '（受限于最大 1.00）' : ''}`);
      });
      lines.push('');
      lines.push('## 多级校核矩阵（相邻链路）');
      if (matrix.pairs.length === 0) {
        lines.push('- 无足够曲线参与校核');
      } else {
        lines.push(`- 总体是否满足：${matrix.overallOk ? '是' : '否'}`);
        matrix.pairs.forEach(p => {
          lines.push(`- 下级“${p.downstream}” ↔ 上级“${p.upstream}”：`);
          lines.push(`  - 最差裕度：${p.worstMargin === null ? '—' : `${p.worstMargin}s`}，I≈${p.worstAtI || '—'}A`);
          lines.push(`  - 是否满足 ${p.minMargin}s：${p.ok ? '是' : '否'}`);
          if (p.recommendation && typeof p.recommendation.recommended === 'number') {
            lines.push(`  - 建议上级 TMS：${p.recommendation.recommended}${p.recommendation.capped ? '（受限于最大 1.00）' : ''}`);
          }
        });
        if (matrix.adjustments.length > 0) {
          lines.push('');
          lines.push('### 顺序调整建议');
          matrix.adjustments.forEach(adj => {
            lines.push(`- 上级“${adj.label}” TMS：${adj.from} → ${adj.to}${adj.capped ? '（受限）' : ''}，最小裕度 ${adj.minMargin}s`);
          });
        }
      }
      const md = lines.join('\n');
      const prefix = (resolvedPrefix && resolvedPrefix.trim()) ? (resolvedPrefix.trim() + '_') : '';
      const filename = `${prefix}TCC_Overlay_Analysis_${Date.now()}.md`;
      downloadTextFile(md, filename, 'text/markdown;charset=utf-8');
    } catch (e) {
      console.error('导出 Markdown 失败', e);
    }
  };

  // 暴露导出方法供外部触发
  useImperativeHandle(ref, () => ({
    exportPng: async () => {
      await handleExportPng();
    },
    exportMd: async () => {
      await handleExportMd();
    },
  }));

  return (
    <Modal
      title="TCC 批量叠加"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
    >
      <Space style={{ marginBottom: 8 }}>
        {sortText ? <Text type="secondary">排序：{sortText}</Text> : null}
        {(selectionOrderLabels && selectionOrderLabels.length > 0) ? (
          <Text type="secondary">{t('overlay.md.selection_order_prefix')}{selectionOrderLabels.join(' → ')}</Text>
        ) : null}
      </Space>
      {series.length < 2 ? (
        <Alert type="info" showIcon message="请至少选择两条含曲线数据的记录进行叠加" />
      ) : null}
      <Space style={{ marginBottom: 8 }}>
        <Text>显示图例：</Text>
        <Switch checked={showLegend} onChange={(v) => {
          setShowLegend(v);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend: v, minMargin, zoomX, zoomY, panX, panY })); } catch {}
        }} />
        <Text>缩放X：</Text>
        <InputNumber min={1} max={8} step={0.5} value={zoomX} onChange={(v) => {
          const val = typeof v === 'number' ? v : 1;
          setZoomX(val);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin, zoomX: val, zoomY, panX, panY })); } catch {}
        }} />
        <Text>缩放Y：</Text>
        <InputNumber min={1} max={8} step={0.5} value={zoomY} onChange={(v) => {
          const val = typeof v === 'number' ? v : 1;
          setZoomY(val);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin, zoomX, zoomY: val, panX, panY })); } catch {}
        }} />
        <Text>平移X：</Text>
        <InputNumber min={-0.9} max={0.9} step={0.05} value={panX} onChange={(v) => {
          const val = typeof v === 'number' ? v : 0;
          setPanX(val);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin, zoomX, zoomY, panX: val, panY })); } catch {}
        }} />
        <Text>平移Y：</Text>
        <InputNumber min={-0.9} max={0.9} step={0.05} value={panY} onChange={(v) => {
          const val = typeof v === 'number' ? v : 0;
          setPanY(val);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin, zoomX, zoomY, panX, panY: val })); } catch {}
        }} />
        <Button onClick={() => {
          setZoomX(1); setZoomY(1); setPanX(0); setPanY(0);
          try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin, zoomX: 1, zoomY: 1, panX: 0, panY: 0 })); } catch {}
        }}>重置视图</Button>
      </Space>
      <div ref={containerRef}>
        <CurveOverlay series={visibleSeries} showLegend={false} zoomX={zoomX} zoomY={zoomY} panX={panX} panY={panY} />
      </div>

      {showLegend && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Typography.Title level={5} style={{ margin: 0 }}>图例与显隐</Typography.Title>
          <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
            {(series || []).map((s) => (
              <div key={s.key || s.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Checkbox
                  checked={!(hiddenLabels.includes(s.key) || hiddenLabels.includes(s.label))}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHiddenLabels((prev) => {
                      const set = new Set(prev);
                      const k = s.key || s.label;
                      if (checked) set.delete(k); else set.add(k);
                      return Array.from(set);
                    });
                  }}
                >{s.label}</Checkbox>
                <ColorPicker
                  value={(colorMap[s.key] || colorMap[s.label] || s.color)}
                  onChange={(color) => {
                    const hex = color.toHexString();
                    const k = s.key || s.label;
                    const next = { ...colorMap, [k]: hex };
                    setColorMap(next);
                    try { localStorage.setItem('tccOverlayColors', JSON.stringify(next)); } catch {}
                  }}
                />
              </div>
            ))}
            <Space>
              <Button onClick={() => {
                setHiddenLabels([]);
                try { localStorage.setItem('tccOverlayHidden', JSON.stringify([])); } catch {}
              }}>显示全部</Button>
              <Button onClick={() => {
                try {
                  const reset = {};
                  (series || []).forEach((s, idx) => { const k = s.key || s.label; reset[k] = palette[idx % palette.length]; });
                  setColorMap(reset);
                  localStorage.setItem('tccOverlayColors', JSON.stringify(reset));
                } catch {}
              }}>重置颜色</Button>
            </Space>
          </Space>
        </>
      )}

      {analyses.length > 0 && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Space style={{ marginBottom: 8 }}>
            <Text>最小选择性裕度(s)：</Text>
            <InputNumber min={0} max={2} step={0.05} value={minMargin} onChange={(v) => {
              const val = typeof v === 'number' ? v : 0.3;
              setMinMargin(val);
              try { localStorage.setItem('tccOverlaySettings', JSON.stringify({ showLegend, minMargin: val, zoomX, zoomY, panX, panY })); } catch {}
            }} />
            <Text type="secondary">以第 1 条曲线为下级(基准)，其余为上级</Text>
          </Space>
          {analyses.map((item) => (
            <div key={item.index} style={{ marginBottom: 8 }}>
              <Typography.Title level={5} style={{ margin: 0 }}>与上级“{item.upstream}”选择性校核</Typography.Title>
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>最差裕度：{item.analysis.worstMargin === null ? '—' : `${item.analysis.worstMargin}s`}，发生在 I≈{item.analysis.worstAtI || '—'}A</Text>
                <Text>是否满足 {minMargin}s：{item.analysis.ok ? '是' : '否'}</Text>
                <Text>建议上级 TMS：{item.recommendation.recommended ?? '—'}{item.recommendation.capped ? '（受限于最大 1.00）' : ''}</Text>
              </Space>
            </div>
          ))}
          {matrix.pairs.length > 0 && (
            <>
              <Divider style={{ margin: '12px 0' }} />
              <Typography.Title level={5} style={{ margin: 0 }}>多级校核矩阵（相邻链路）</Typography.Title>
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                <Text>总体是否满足：{matrix.overallOk ? '是' : '否'}</Text>
                {matrix.pairs.map((p) => (
                  <div key={`${p.downstream}__${p.upstream}`} style={{ marginBottom: 6 }}>
                    <Text>下级“{p.downstream}” ↔ 上级“{p.upstream}”</Text>
                    <div>
                      <Text type="secondary">最差裕度：{p.worstMargin === null ? '—' : `${p.worstMargin}s`}，I≈{p.worstAtI || '—'}A；是否满足 {p.minMargin}s：{p.ok ? '是' : '否'}</Text>
                    </div>
                    {p.recommendation && typeof p.recommendation.recommended === 'number' && (
                      <div>
                        <Text type="secondary">建议上级 TMS：{p.recommendation.recommended}{p.recommendation.capped ? '（受限于最大 1.00）' : ''}</Text>
                      </div>
                    )}
                  </div>
                ))}
                {matrix.adjustments.length > 0 && (
                  <div>
                    <Text>顺序调整建议：</Text>
                    {matrix.adjustments.map(adj => (
                      <div key={`adj_${adj.indexUpstream}`}>
                        <Text type="secondary">上级“{adj.label}” TMS：{adj.from} → {adj.to}{adj.capped ? '（受限）' : ''}，最小裕度 {adj.minMargin}s</Text>
                      </div>
                    ))}
                  </div>
                )}
              </Space>
            </>
          )}
        </>
      )}

      <Space style={{ marginTop: 12 }}>
        <Text type="secondary">叠加数量：{visibleSeries.length}</Text>
        <Button type="primary" onClick={handleExportPng} disabled={series.length === 0}>导出叠加图 PNG</Button>
        <Button onClick={handleExportMd} disabled={analyses.length === 0}>导出校核结果 Markdown</Button>
        <Button onClick={onClose}>关闭</Button>
      </Space>
    </Modal>
  );
});

export default TccOverlayModal;